import React, { useCallback, useState } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";

interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

/**
 * Lightweight snackbar helper built on MUI Snackbar + Alert.
 *
 * Usage:
 *   const { showSnackbar, snackbar } = useSnackbar();
 *   showSnackbar('Something went wrong', 'error');
 *   return (<>{snackbar} ...</>);
 */
export const useSnackbar = () => {
    const [state, setState] = useState<SnackbarState>({
        open: false,
        message: "",
        severity: "error",
    });

    const showSnackbar = useCallback(
        (message: string, severity: AlertColor = "error") => {
            setState({ open: true, message, severity });
        },
        []
    );

    const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setState((prev) => ({ ...prev, open: false }));
    }, []);

    const snackbar = (
        <Snackbar
            open={state.open}
            autoHideDuration={5000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Alert
                onClose={handleClose}
                severity={state.severity}
                variant="filled"
                sx={{ width: "100%" }}
            >
                {state.message}
            </Alert>
        </Snackbar>
    );

    return { showSnackbar, snackbar };
};

export default useSnackbar;
